import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Calendar,
  Languages,
  Mail,
  MapPin,
  ScrollText,
  Settings,
  UserCheck,
  Users,
} from "lucide-react";
import { splitName } from "@/lib/split-name";
import { useProfileUrl, type ProfileSection } from "@/hooks/use-profile-url";
import { useInstallPrompt } from "@/hooks/use-install-prompt";
import { ProfileDialog, type ProfileNavLink } from "./profile-dialog";
import type { RoleKind } from "@/components/vroom/role-pill";

interface ProfileDialogContextValue {
  openProfile: (section?: ProfileSection) => void;
  closeProfile: () => void;
  profileOpen: boolean;
}

const NOOP_CTX: ProfileDialogContextValue = {
  openProfile: () => {},
  closeProfile: () => {},
  profileOpen: false,
};

const Ctx = React.createContext<ProfileDialogContextValue>(NOOP_CTX);

export function useProfileDialog(): ProfileDialogContextValue {
  return React.useContext(Ctx);
}

export interface ProfileDialogProviderProps {
  user: { name: string; email: string; role?: RoleKind };
  children: React.ReactNode;
}

export function ProfileDialogProvider({ user, children }: ProfileDialogProviderProps) {
  const { t } = useTranslation();
  const { profileSection, profileOpen, setProfile } = useProfileUrl();
  const navigate = useNavigate();
  const { canInstall, install } = useInstallPrompt();

  const openProfile = React.useCallback(
    (section: ProfileSection = "account") => setProfile(section),
    [setProfile],
  );
  const closeProfile = React.useCallback(() => setProfile(undefined, true), [setProfile]);

  const goTo = React.useCallback(
    (to: string) => () => {
      setProfile(undefined, true);
      void navigate({ to });
    },
    [navigate, setProfile],
  );

  const role: RoleKind = user.role ?? "worker";
  const isAdmin = role === "super_admin";

  const beheerLinks: ProfileNavLink[] | undefined = isAdmin
    ? [
        {
          icon: <Building2 size={16} />,
          label: t("admin.sidebar.cost-centers.label"),
          onClick: goTo("/dashboard"),
        },
        {
          icon: <Users size={16} />,
          label: t("admin.sidebar.users.label"),
          onClick: goTo("/admin/users"),
        },
        {
          icon: <UserCheck size={16} />,
          label: t("admin.sidebar.replacements.label"),
          onClick: goTo("/admin/replacements"),
        },
        {
          icon: <MapPin size={16} />,
          label: t("admin.sidebar.addresses.label"),
          onClick: goTo("/admin/addresses"),
        },
        {
          icon: <Calendar size={16} />,
          label: t("admin.sidebar.holidays.label"),
          onClick: goTo("/admin/holidays"),
        },
      ]
    : undefined;

  const systeemLinks: ProfileNavLink[] | undefined = (() => {
    const links: ProfileNavLink[] = [];
    if (isAdmin) {
      links.push(
        {
          icon: <ScrollText size={16} />,
          label: t("admin.sidebar.audit-log.label"),
          onClick: goTo("/admin/audit-log"),
        },
        {
          icon: <Mail size={16} />,
          label: t("admin.sidebar.email-templates.label"),
          onClick: goTo("/admin/email-templates"),
        },
        {
          icon: <Languages size={16} />,
          label: t("admin.sidebar.translations.label"),
          onClick: goTo("/admin/translations"),
        },
        {
          icon: <Settings size={16} />,
          label: t("admin.sidebar.app-settings.label"),
          onClick: goTo("/admin/app-settings"),
        },
      );
    }
    if (canInstall) {
      links.push({
        label: t("common.installApp.cta"),
        onClick: () => void install(),
      });
    }
    return links.length > 0 ? links : undefined;
  })();

  const { firstName, lastName } = splitName(user.name);

  const onSignOut = React.useCallback(async () => {
    closeProfile();
    await navigate({ to: "/sign-out" });
  }, [closeProfile, navigate]);

  return (
    <Ctx.Provider value={{ openProfile, closeProfile, profileOpen }}>
      {children}
      <ProfileDialog
        open={profileOpen}
        onOpenChange={(open) =>
          open ? setProfile(profileSection ?? "account") : setProfile(undefined, true)
        }
        activeSection={profileSection ?? "account"}
        onSectionChange={(section) => setProfile(section, true)}
        user={{
          firstName,
          lastName,
          email: user.email,
          role,
        }}
        onSignOut={() => void onSignOut()}
        beheerLinks={beheerLinks}
        systeemLinks={systeemLinks}
      />
    </Ctx.Provider>
  );
}
